/* eslint-disable no-unused-vars */
/* eslint-disable react/default-props-match-prop-types */
/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useMemo } from 'react';
import _ from 'underscore';

import { FilePicker } from '@bingads-webui-react/file-picker';
import type {
  FileListChangeHandler,
  FilePickerWithoutStylesProps,
  FileLike,
  IFileInput,
  IFilePickerResult,
} from '@bingads-webui-react/file-picker';
import { FilePickerProgress as DefaultFilePickerProgress } from '@bingads-webui-react/file-picker-progress';
import type {
  FilePickerProgressProps,
  IFileItem,
  I18nModel as FilePickerProgressI18nModel,
} from '@bingads-webui-react/file-picker-progress';
import { useFileUploadProgress as useDefaultFileUploadProgress } from '@bingads-webui-react/use-file-upload-progress';
import type {
  FilesUploadCompletedInfo,
  IFileObject,
  IUseFileUploadProgressOptions,
} from '@bingads-webui-react/use-file-upload-progress';

export type FilePickerWithProgressFile = IFileInput & IFileItem;
export type FilePickerWithProgressFileList = FilePickerWithProgressFile[];
export type FilePickerWithProgressChangeHandler = FileListChangeHandler;
export type FileUploadCompletedInfoChangeHandler = (filesUploadCompletedInfo: FilesUploadCompletedInfo) => void;
export type UseFileUploadProgress = typeof useDefaultFileUploadProgress;
export type FilePickerProgressComponent = React.ComponentType<FilePickerProgressProps>;

export interface IFilePickerWithProgressProps extends Omit<
  FilePickerWithoutStylesProps,
  'classes' | 'fileList' | 'i18n' | 'onFileChange' | 'validator'
> {
  i18n: FilePickerProgressI18nModel;
  fileList: FilePickerWithProgressFileList;
  onFileChange?: FilePickerWithProgressChangeHandler;
  validator?: (file: FileLike) => string | null | undefined | void;
  onFileCompletedInfoChange?: FileUploadCompletedInfoChangeHandler;
  additionalFormData?: IUseFileUploadProgressOptions['additionalFormData'];
  initialFilesUploadCompletedInfo?: FilesUploadCompletedInfo;
  endPoint: string;
  useFileUploadProgress?: UseFileUploadProgress;
  FilePickerProgress?: FilePickerProgressComponent;
}


export const FilePickerWithProgress: React.FC<IFilePickerWithProgressProps> = (props) => {
  const {
    initialFilesUploadCompletedInfo,
    additionalFormData,
    fileList,
    onFileChange,
    onFileCompletedInfoChange,
    endPoint,
    i18n,
    useFileUploadProgress,
    FilePickerProgress,
  } = props;

  const {
    filesUploadProgressInfo,
    filesUploadCompletedInfo,
    uploadFileWithProgressFromList,
  } = useFileUploadProgress({
    initialFilesUploadCompletedInfo,
    additionalFormData,
    endPoint,
  });

  useEffect(() => {
    onFileCompletedInfoChange(filesUploadCompletedInfo);
  }, [filesUploadCompletedInfo, onFileCompletedInfoChange]);

  const onDocumentationFileChange = useCallback((filesState: IFilePickerResult) => {
    uploadFileWithProgressFromList(filesState.fileList as IFileObject[]);
    onFileChange(filesState);
  }, [onFileChange, uploadFileWithProgressFromList]);
  const filePickerUniqeClassName = useMemo(() => _.uniqueId('file-picker'), []);

  return (
    <React.Fragment>
      <FilePicker
        {...props}
        className={`${props.className} ${filePickerUniqeClassName}`}
        onFileChange={onDocumentationFileChange}
        validator={props.validator as FilePickerWithoutStylesProps['validator']}
      />
      {
        fileList &&
        <FilePickerProgress
          i18n={i18n}
          filePickerCssSelector={`.${filePickerUniqeClassName}`}
          fileList={fileList}
          filesUploadProgressInfo={filesUploadProgressInfo}
          filesUploadCompletedInfo={filesUploadCompletedInfo}
        />
      }
    </React.Fragment>);
};


FilePickerWithProgress.defaultProps = {
  className: '',
  fileList: [],
  maxCount: 10,
  validator: _.noop,
  allowedFileExtensions: [],
  required: false,
  alwaysShowError: false,
  shouldAutoRename: false,
  passiveErrorMessages: [],
  customPlaceholder: '',
  onFileCompletedInfoChange: _.noop,
  onFileChange: _.noop,
  additionalFormData: {},
  initialFilesUploadCompletedInfo: {},
  useFileUploadProgress: useDefaultFileUploadProgress,
  FilePickerProgress: DefaultFilePickerProgress,
};
